import React from "react";
import { observer, Provider } from "mobx-react";
import { VariantModel } from "../../models/VariantModel";
import { ModelwalkModel } from "../../models/ModelwalkModel";
import ModelWalkStore from "../../stores/ModelWalkStore";
import ModelWalkCard from "./ModelWalkCard";
import ModelWalkHeader from "./ModelWalkHeader";
import RepaymentStore from "../../stores/RepaymentStore";

interface ModelWalkState {
  currentIndex: number;
  store: ModelWalkStore;
  repaymentStore: RepaymentStore;
}

interface ModelWalkProps {
  content: ModelwalkModel;
  comparisonMode?: boolean;
  onUpdate?: Function;
  startIndex?: number;
  includeHeader?: boolean;
  enableStaticPricingByState?: boolean;
  modelDisclaimer?: any;
  hidePriceDisclaimer?: boolean;
  priceDisclaimerOverride?: string;
  toggleVariant?: Function;
  setVariantGrouping?: Function;
  hideRepayment: boolean;
}

@observer
class ModelWalk extends React.Component<ModelWalkProps, ModelWalkState> {
  startIndex: number = 1;

  get cardWidth(): number {
    return this.state.store.breakpoint.isMobile ? 76 : 33.33; //CSS WIDTHS
  }

  get variants(): VariantModel[] {
    const { variants, showSedan, showVariantGrouping, modelName } =
      this.state.store;
    if (!variants) return [];
    if (showVariantGrouping && modelName.toLowerCase() === "wrx") {
      if (showSedan) {
        return variants.filter(
          (v) => v.variantType == "Sedan" && !v.hideFromModelWalk
        );
      } else {
        return variants.filter(
          (v) => v.variantType == "Sportswagon" && !v.hideFromModelWalk
        );
      }
    } else {
      return variants.filter((v) => !v.hideFromModelWalk);
    }
  }

  get hasPrev(): boolean {
    return this.state.currentIndex > this.startIndex;
  }

  get hasNext(): boolean {
    if (
      this.state.currentIndex >= this.variants.length - 3 &&
      !this.state.store.breakpoint.isMobile &&
      this.props.comparisonMode
    ) {
      return false;
    }
    if (this.state.store.breakpoint.isMobile) {
      return !(this.variants.length - 1 === this.state.currentIndex);
    } else {
      if (this.variants.length > 2) {
        return !(this.variants.length - 2 === this.state.currentIndex);
      }
      return false;
    }
  }

  get leftPos(): number {
    const { currentIndex, store } = this.state;
    const { comparisonMode } = this.props;
    if (store.breakpoint.isMobile) {
      if (currentIndex === 0) return 10;
      return (currentIndex * this.cardWidth - (100 - this.cardWidth) / 2) * -1;
    }

    if (comparisonMode) {
      if (this.variants.length < 3) {
        return this.cardWidth / this.variants.length;
      } else {
        return currentIndex * this.cardWidth * -1;
      }
    } else {
      if (this.variants.length < 3) {
        return this.cardWidth / this.variants.length;
      } else {
        return (currentIndex - 1) * this.cardWidth * -1;
      }
    }
  }

  get allVariantPriceHidden(): boolean {
    return !this.variants.some((x) => !x.hidePriceDetails);
  }

  touches: any = {
    startX: 0,
    endX: 0,
  };

  constructor(props: ModelWalkProps) {
    super(props);
    const store = new ModelWalkStore(
      props.content.variants,
      props.enableStaticPricingByState,
      props.content.showVariantGrouping,
      props.content.modelName
    );
    let repaymentStore = null;
    if (!props.hideRepayment) {
      repaymentStore = new RepaymentStore(store);
    }

    if (typeof props.startIndex !== "undefined") {
      this.startIndex = props.startIndex;
    }

    if (store.breakpoint.isMobile) {
      this.startIndex = 0;
    }

    this.state = {
      currentIndex: this.startIndex,
      store: store,
      repaymentStore: repaymentStore,
    };
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.resetIndex = this.resetIndex.bind(this);
  }

  componentDidMount(): void {
    const variants = this.props.content.variants;
    const model =
      variants && variants.length > 0 ? variants.map((x) => x.model)[0] : "";

    window["pushDigitalData"]({
      event: "_pageLoaded",
      page: {
        vehicle: {
          make: "Subaru",
          model: `${model}`,
          type: "new",
          marketingCategory: `${
            !!this.props.content.marketingCategory
              ? this.props.content.marketingCategory
              : ""
          }`,
        },
      },
    });
  }

  resetIndex() {
    let index = 0;

    if (!this.state.store.breakpoint.isMobile) {
      index = 1;
    }
    if (this.props.comparisonMode && !this.state.store.breakpoint.isMobile) {
      index = 0;
    }

    this.setState({
      currentIndex: index,
    });

    if (this.props.onUpdate) {
      this.props.onUpdate(index);
    }
  }

  next() {
    const { currentIndex } = this.state;
    const newIndex = currentIndex + 1;

    if (!this.hasNext) return;

    if (currentIndex >= this.variants.length - 1) {
      return false;
    }

    this.setState({
      currentIndex: newIndex,
    });

    if (this.props.onUpdate) {
      this.props.onUpdate(newIndex);
    }
  }

  prev() {
    const { currentIndex } = this.state;
    const newIndex = currentIndex - 1;

    if (!this.hasPrev) return;

    if (currentIndex <= this.startIndex) {
      return false;
    }

    this.setState({
      currentIndex: newIndex,
    });

    if (this.props.onUpdate) {
      this.props.onUpdate(newIndex);
    }
  }

  handleTouchStart(e) {
    this.touches.startX = e.changedTouches[0].screenX;
  }

  handleTouchEnd(e) {
    this.touches.endX = e.changedTouches[0].screenX;
    const difference = this.touches.endX - this.touches.startX;
    if (difference > 100) return this.prev();
    if (difference < -100) return this.next();
  }

  render() {
    const {
      currentIndex,
      store,
      store: {
        toggleDisplayedVariant,
        showSedan,
        showVariantGrouping,
        modelName,
      },
    } = this.state;
    const {
      enableStaticPricingByState,
      modelDisclaimer,
      hidePriceDisclaimer,
      priceDisclaimerOverride,
      hideRepayment,
    } = this.props;
    const showModelDisclaimer =
      (modelDisclaimer && modelDisclaimer.length > 0) || false;

    return (
      <Provider repaymentStore={this.state.repaymentStore}>
        <section
          className={`modelWalk inner ${
            this.props.comparisonMode ? "modelWalk--comparisonMode" : ""
          }`}
        >
          {this.hasNext || this.hasPrev ? (
            <div className="modelWalk__controls">
              <div
                className={`modelWalk__control modelWalk__control--next ${
                  this.hasNext ? "" : "modelWalk__control--disabled"
                }`}
                onClick={(e) => this.next()}
              >
                next
              </div>
              <div
                className={`modelWalk__control modelWalk__control--prev ${
                  this.hasPrev ? "" : "modelWalk__control--disabled"
                }`}
                onClick={(e) => this.prev()}
              >
                prev
              </div>
            </div>
          ) : null}

          {showVariantGrouping && modelName.toLowerCase() === "wrx" && (
            <div className="row">
              <div className="variantToggle">
                <ul className="variantToggle__list">
                  <li
                    className={`variantToggle__item_sedan ${
                      showSedan ? "active" : ""
                    }`}
                    onClick={() => {
                      this.resetIndex();
                      toggleDisplayedVariant(true);
                      this.props.toggleVariant(true);
                    }}
                  >
                    Sedan
                  </li>
                  <li
                    className={`variantToggle__item ${
                      !showSedan ? "active" : ""
                    }`}
                    onClick={() => {
                      this.resetIndex();
                      toggleDisplayedVariant(false);
                      this.props.toggleVariant(false);
                    }}
                  >
                    Sportswagon
                  </li>
                </ul>
              </div>
            </div>
          )}

          <div
            className="modelWalk__modelsWrap"
            onTouchStart={(e) => this.handleTouchStart(e)}
            onTouchEnd={(e) => this.handleTouchEnd(e)}
          >
            <div
              className="modelWalk__models"
              style={{ transform: `translateX(${this.leftPos}%)` }}
            >
              {this.variants.map((variant: VariantModel, id: number): any => {
                let className = "modelWalk__model";

                if (id === currentIndex) {
                  className = `${className}--current`;
                }

                return (
                  <ModelWalkCard
                    store={store}
                    key={`carModel__${id}`}
                    leftPos={id * this.cardWidth}
                    className={className}
                    carDetails={variant}
                    comparisonMode={!!this.props.comparisonMode}
                    allVariantPriceHidden={this.allVariantPriceHidden}
                    enableStaticPricingByState={
                      this.props.enableStaticPricingByState
                    }
                    hideRepayment={!!this.props.hideRepayment}
                  />
                );
              })}
            </div>
          </div>
          {hideRepayment &&
            !hidePriceDisclaimer &&
            (!priceDisclaimerOverride ||
              priceDisclaimerOverride.length === 0) && (
              <div className="modelWalk__priceDisclaimer modelWalk__priceDisclaimer--full">
                Recommended Driveaway Price (RDP)&nbsp;
                {`${
                  store.suburb
                    ? `shown based on ${store.suburb.name}, ${store.suburb.postcode}`
                    : ""
                }`}
                . RDP may change based on location.&nbsp;
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    store.showPostcodeOverlayActive();
                  }}
                >
                  Enter your postcode&nbsp;
                </a>
                for the RDP in your area.
              </div>
            )}
          {!hidePriceDisclaimer &&
            priceDisclaimerOverride &&
            priceDisclaimerOverride.length > 0 && (
              <div className="modelWalk__priceDisclaimer modelWalk__priceDisclaimer--full">
                {priceDisclaimerOverride}
              </div>
            )}
          {showModelDisclaimer && (
            <div className="modelWalk__priceDisclaimer modelWalk__priceDisclaimer--full">
              {modelDisclaimer}
            </div>
          )}

          {enableStaticPricingByState && (
            <div className="modelWalk__priceDisclaimer modelWalk__priceDisclaimer--full">
              Final on road costs may vary according to individual circumstances
              and actual driveaway price may vary between Retailers. <br />
              Ask your local Retailer for confirmation of their driveaway price.
            </div>
          )}

          {this.props.includeHeader ? (
            <ModelWalkHeader
              store={store}
              currentIndex={currentIndex}
              hasNext={this.hasNext}
              next={this.next}
              hasPrev={this.hasPrev}
              prev={this.prev}
            />
          ) : null}
        </section>
      </Provider>
    );
  }
}

export default ModelWalk;
